/**
 * The one owner of safe `localStorage` access in the frontend.
 *
 * Storage here is advisory ("remember what I chose"), never a source of truth:
 * an unavailable or throwing storage reads as `null`, and a failed write is
 * logged and dropped. `chat-persistence.ts` (the `crewstack:chat:` helpers) and
 * `createLocalStorageStore` both go through the accessors below, so there is
 * one failure policy.
 *
 * `createLocalStorageStore` adds the two contracts `useSyncExternalStore`
 * needs: `getSnapshot` returns the SAME decoded value while the raw string is
 * unchanged (a fresh object per call would re-render forever), and a write in
 * this document notifies this document's subscribers itself, because the
 * browser's `storage` event fires only in OTHER documents.
 */
export type StorageAccess = () => Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;

export function browserLocalStorage(): Storage | null {
	if (typeof window === 'undefined') return null;
	try {
		return window.localStorage;
	} catch {
		return null;
	}
}

/**
 * The tab's `sessionStorage`, through the same accessors and failure policy. For state that must
 * follow one tab across a same-origin full-page round trip and nothing else.
 */
export function browserSessionStorage(): Storage | null {
	if (typeof window === 'undefined') return null;
	try {
		return window.sessionStorage;
	} catch {
		return null;
	}
}

export function readLocalStorageItem(key: string, storageAccess: StorageAccess = browserLocalStorage): string | null {
	const storage = storageAccess();
	if (storage === null) return null;
	try {
		return storage.getItem(key);
	} catch {
		return null;
	}
}

/** Returns whether the value was stored; a failure is logged, never thrown. */
export function writeLocalStorageItem(
	key: string,
	value: string,
	storageAccess: StorageAccess = browserLocalStorage,
): boolean {
	const storage = storageAccess();
	if (storage === null) return false;
	try {
		storage.setItem(key, value);
		return true;
	} catch (error) {
		console.warn(`[local-storage] could not write ${key}`, error);
		return false;
	}
}

export function removeLocalStorageItem(key: string, storageAccess: StorageAccess = browserLocalStorage): void {
	const storage = storageAccess();
	if (storage === null) return;
	try {
		storage.removeItem(key);
	} catch (error) {
		console.warn(`[local-storage] could not remove ${key}`, error);
	}
}

export type LocalStorageStore<T> = {
	readonly subscribe: (listener: () => void) => () => void;
	readonly getSnapshot: () => T;
	readonly write: (value: T) => void;
};

export function createLocalStorageStore<T>(options: {
	readonly key: string;
	/** Receives `null` when the key is absent or storage is unavailable; must not throw. */
	readonly decode: (raw: string | null) => T;
	readonly encode: (value: T) => string;
	readonly storageAccess?: StorageAccess;
}): LocalStorageStore<T> {
	const storageAccess = options.storageAccess ?? browserLocalStorage;
	const listeners = new Set<() => void>();
	let cachedRaw: string | null = null;
	let cachedValue: T = options.decode(null);

	const notify = () => {
		for (const listener of listeners) listener();
	};

	const onStorage = (event: StorageEvent) => {
		if (event.key === null || event.key === options.key) notify();
	};

	return {
		subscribe(listener) {
			listeners.add(listener);
			if (listeners.size === 1 && typeof window !== 'undefined') {
				window.addEventListener('storage', onStorage);
			}
			return () => {
				listeners.delete(listener);
				if (listeners.size === 0 && typeof window !== 'undefined') {
					window.removeEventListener('storage', onStorage);
				}
			};
		},
		getSnapshot() {
			const raw = readLocalStorageItem(options.key, storageAccess);
			if (raw !== cachedRaw) {
				cachedRaw = raw;
				cachedValue = options.decode(raw);
			}
			return cachedValue;
		},
		write(value) {
			if (writeLocalStorageItem(options.key, options.encode(value), storageAccess)) notify();
		},
	};
}
